import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

const TableOuterContainer = styled.div`
    width: 100%;
    height: 100%;
    overflow: auto;
    background-color: white;
    border-radius: 6px;
    padding: 12px 30px;
`;

const TableInnerContainer = styled.div`
    min-width: 1200px;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    border-bottom: 1px solid #e8e8e8;
`;

const HeaderItemWrapper = styled.div`
    flex: 1;
    padding: 12px 24px;
    color: black;
`;

const ContentOuterWrapper = styled.div`
    flex: 1;
    position: relative;
    overflow: auto;
`;

const ContentInnerWrapper = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
`;

const ContentRowWrapper = styled.div`
    display: flex;
    flex-direction: row;
    border-bottom: 1px solid #e8e8e8;
`;

const Table = ({ columns = [], rows = [] }) => {
    const [rowsData, setRowsData] = useState(rows);

    const handleSubmit = async (data, onSubmit) => {
        const { id, key, value } = data;
        try {
            if (onSubmit) await onSubmit({ id, key, value });
            setRowsData(
                rowsData.map((item) => {
                    if (item.id === id) return { ...item, [key]: value };
                    return item;
                })
            );
        } catch (err) {}
    };

    useEffect(() => {
        setRowsData(rows);
    }, [rows]);

    return (
        <TableOuterContainer>
            <TableInnerContainer>
                <HeaderWrapper>
                    {columns.map((column) => {
                        return <HeaderItemWrapper key={column.key}>{column.name}</HeaderItemWrapper>;
                    })}
                </HeaderWrapper>
                <ContentOuterWrapper>
                    <ContentInnerWrapper>
                        {rowsData.map((row) => {
                            return (
                                <ContentRowWrapper key={row.id}>
                                    {columns.map((column) => {
                                        const value = row[column.key];
                                        return (
                                            <ContentCell
                                                id={row.id}
                                                key={column.key}
                                                value={value}
                                                validate={column.validate}
                                                content={column.content}
                                                onSubmit={({ id, value }) =>
                                                    handleSubmit({ id, key: column.key, value }, column.onSubmit)
                                                }
                                                active={row.active}
                                                disabled={column.disabled}
                                            />
                                        );
                                    })}
                                </ContentRowWrapper>
                            );
                        })}
                    </ContentInnerWrapper>
                </ContentOuterWrapper>
            </TableInnerContainer>
        </TableOuterContainer>
    );
};

const InputBoxCell = styled.input`
    flex: 1;
    box-sizing: border-box;
    padding: 12px 24px;
    margin: 2px;
    border: none;
    &:disabled {
        background-color: transparent;
    }
    ${(props) =>
        !props.active &&
        css`
            color: #d7d5d5;
        `}
`;

const ContentBoxCell = styled.div`
    flex: 1;
    box-sizing: border-box;
    padding: 12px 24px;
    margin: 2px;
    ${(props) =>
        !props.active &&
        css`
            color: #d7d5d5;
        `}
`;

const ContentCell = (props) => {
    const { id, value, validate = {}, content, onSubmit, active, disabled } = props;
    const { type, regex, action, onError } = validate;

    const inputRef = useRef(null);

    const handleBlur = async () => {
        const currentValue = inputRef.current.value;
        if (currentValue === value) return;

        // validate if input is correct
        let valid = true;
        switch (type) {
            case 'action':
                valid = await action({ id, value: currentValue });
                break;
            case 'regex':
                const re = new RegExp(regex);
                valid = re.test(currentValue);
                break;
            default:
                break;
        }

        if (valid) {
            if (onSubmit) onSubmit({ id, value: currentValue });
            return;
        }

        // consequence when input is incorrect
        inputRef.current.value = value || '';
        if (onError) onError();
    };

    const handleKeyDown = (e) => {
        // press esc
        if ([27].includes(e.keyCode)) {
            inputRef.current.value = value;
            inputRef.current.blur();
        }

        // press enter
        if ([13].includes(e.keyCode)) {
            inputRef.current.blur();
        }
    };

    return (
        <>
            {content ? (
                <ContentBoxCell active={active}>{content.apply(props)}</ContentBoxCell>
            ) : (
                <InputBoxCell
                    disabled={disabled || !active}
                    active={active}
                    type='text'
                    ref={inputRef}
                    defaultValue={value}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                />
            )}
        </>
    );
};

export default Table;
